// A.THINK
import { useState, useEffect, useRef, useMemo, useReducer } from 'react'

import useInitParams from './useInitParams'
import usePrevious from 'hooks/usePrevious'

import { Action, reducer } from './reducer-data'
import { checkKeyInObject, getMultiSelectParams } from './utils'
import { pageKeys, Key, IInitValues } from 'constants/search-data/search-data'

import { IObj } from 'constants/types/common.types'
import { IBasicCard, IEventCard } from 'constants/types/card.types'
import { IInitParams, IParam, IgetCardsMaterials } from 'constants/types/search.types'

interface IUseCommonSearch {
  InitValues: IInitValues
  selectedOption: string
  getCardsMaterials: IgetCardsMaterials
  selectOptions?: IObj<any>
  setInitState?: React.Dispatch<React.SetStateAction<boolean>>
  isInitState?: boolean
  isMobile?: boolean
  isAuthorized?: boolean
}

const useCommonSearch = ({
  InitValues,
  selectedOption,
  getCardsMaterials,
  setInitState,
  selectOptions,
  isMobile,
  isAuthorized,
}: IUseCommonSearch) => {
  const [isLoading, setLoading] = useState(false)
  const { initParams, queryParams } = useInitParams()

  const initData: IInitValues = useMemo(
    () => ({
      ...InitValues,
      params: {
        ...InitValues.params,
        [selectedOption]: {
          ...InitValues.params[selectedOption],
          ...initParams,
        },
      },
    }),
    []
  )

  const [data, dispatch] = useReducer(reducer, initData)

  const inUse = useRef(false) //идет запрос к БД
  const callStack = useRef<(() => void)[]>([])
  const prevPages = usePrevious(data.pages)

  /** Функция, чтобы не спамить запросы карточек при быстром выборе фильтров. Определяет идет ли сейчас запрос,
   * если да - то добавляет коллбэк в очередь. Когда освободится вызывает последний колбэк из очереди  */
  const checkInUse = (callback: () => void) => {
    if (inUse.current) {
      callStack.current.push(callback)
    } else {
      inUse.current = true
      Promise.resolve(callback())
        .then(() => {
          if (callStack.current.length) {
            callStack.current[callStack.current.length - 1]()
            callStack.current = []
          }
        })
        .then(() => {
          inUse.current = false
        })
    }
  }

  useEffect(() => {
    if (
      prevPages &&
      prevPages[selectedOption].currentPage <= data.pages[selectedOption].currentPage
    )
      checkInUse(() => getCards(selectedOption))
  }, [data.pages[selectedOption]?.currentPage])

  /*Перезапрос карточек при выборе фильтраа и сброс значени до 0 текущей страницы */
  useEffect(() => {
    checkInUse(() => getCards(selectedOption, true))
  }, [selectedOption, data.params[selectedOption]])

  /* Выставляет параметры фильтров из ссылки, selectOptions в зависимостях потому что они приходят позже из запроса */
  useEffect(() => {
    if (!selectOptions) return
    const currentParams = {} as IInitParams

    if (checkKeyInObject(queryParams, Key.LECTURS) && selectOptions[Key.LECTURS]?.length) {
      currentParams[Key.LECTURS] = getMultiSelectParams(
        queryParams[Key.LECTURS] as string,
        selectOptions[Key.LECTURS]
      )
    }

    if (checkKeyInObject(queryParams, Key.THEMES) && selectOptions[Key.THEMES]?.length) {
      currentParams[Key.THEMES] = getMultiSelectParams(
        queryParams[Key.THEMES] as string,
        selectOptions[Key.THEMES]
      )
    }

    if (
      checkKeyInObject(queryParams, Key.SPECIALITIES) &&
      selectOptions[Key.SPECIALITIES]?.length
    ) {
      currentParams[Key.SPECIALITIES] = getMultiSelectParams(
        queryParams[Key.SPECIALITIES] as string,
        selectOptions[Key.SPECIALITIES]
      )
    }
    if (checkKeyInObject(queryParams, Key.POINTS)) {
      currentParams[Key.POINTS] = getMultiSelectParams(
        queryParams[Key.POINTS] as string,
        selectOptions[Key.POINTS]
      )
    }

    dispatch({
      type: Action.SET_PARAMETERS,
      selected: selectedOption,
      payload: currentParams,
    })
  }, [
    selectOptions?.[Key.LECTURS],
    selectOptions?.[Key.THEMES],
    selectOptions?.[Key.SPECIALITIES],
    selectOptions?.[Key.LECTURS],
    selectOptions?.[Key.POINTS],
  ])

  /**
   При infinite скролле карточки подгружаются частями, по страницам
   * @param amountPages - количество страниц с карточками
   * @param key - засетить сколько всего страниц будет или сколько сейчас загружено (pageKeys.TOTAL или в pageKeys.CURRENT)
   * @param selected - для какой опции
   */
  const handlePageData = (amountPages: number, key: string, selected: string) => {
    dispatch({
      type: Action.SET_PAGE_INFO,
      amountPages,
      key,
      selected,
    })
  }

  /** Заменяет карточки для опции
   * @param newCards - карточки
   * @param option - опция, по дефолту берет ту, которая выбрана сейчас
   */
  const handleCards = (newCards: IBasicCard[] | IEventCard[], option: string = selectedOption) => {
    dispatch({
      type: Action.CHANGE_CARD,
      selected: option,
      payload: newCards,
    })
  }

  /**
   * @param selected - для какой опции запрашивать карточки
   * @param manualReset - сделать сброс карточек и снова запросить только первую страницу
   */
  const getCards = async (selected: string, manualReset?: boolean) => {
    setLoading(true)
    const reset = manualReset || false
    const usedCardsCount = Number(data.params[selected].page || 0)

    const { totalPages, allItems, allCount } = await getCardsMaterials(
      data.params[selected],
      false,
      usedCardsCount,
      selected,
      isMobile,
      isAuthorized
    )
    dispatch({
      type: Action.SET_GENERAL_COUNT,
      selected: selectedOption,
      payload: allCount,
    })

    if (reset) {
      dispatch({
        type: Action.RESET_PAGE_INFO,
        selected: selectedOption,
        totalPages,
      })
    } else handlePageData(totalPages, pageKeys.TOTAL, selectedOption)

    if (reset) {
      dispatch({ type: Action.RESET_CARDS, selected, payload: allItems })
    } else {
      dispatch({
        type: Action.ADD_CARD,
        selected,
        payload: allItems,
      })
    }

    setLoading(false)
  }

  /** Засетить параметр текущей опции */
  const handleParameterSet = (key: string, value: IParam) => {
    setInitState && setInitState(false)

    dispatch({ type: Action.SET_PARAMETER, selected: selectedOption, key, value })

    if (key !== Key.PAGE) {
      dispatch({ type: Action.SET_PARAMETER, selected: selectedOption, key: Key.PAGE, value: '1' })
    }
  }

  /** Сбросить параметры у текущей опции */
  const handleReset = () => {
    dispatch({
      type: Action.RESET_PARAMETER,
      selected: selectedOption,
      payload: InitValues,
    })
  }

  /** Запросить следующую страничку карточек, если она есть */
  const handlePageChange = (): void => {
    const nextPage = data.pages[selectedOption].currentPage + 1
    if (nextPage < data.pages[selectedOption].totalPagesCount) {
      setLoading(true)
      setTimeout(() => {
        handlePageData(nextPage, pageKeys.CURRENT, selectedOption)
      }, 400)
    }
  }

  return {
    data,
    isLoading,
    handleCards,
    getCards,
    handleParameterSet,
    handleReset,
    handlePageChange,
  }
}

export default useCommonSearch
